// ---------------------------------------------------------------------------
// TariffStep — Shared for dso_tariff + retailer_tariff
// ---------------------------------------------------------------------------
// When retailer_tariff options include a "custom" option (metadata.is_custom),
// it renders as a distinct CTA below the real tariff cards.
// ---------------------------------------------------------------------------

import { memo, useCallback, useMemo, useState } from 'react';
import { OptionCard } from '@/components/shared/OptionCard';
import {
  PropertyFilters,
  filterByProperties,
  getFilterableKeys,
} from '@/components/shared/PropertyFilters';
import { SearchInput } from '@/components/shared/SearchInput';
import { StepHeading } from '@/components/shared/StepHeading';
import { t } from '@/i18n';
import { getStepIcon } from '@/utils/stepIcons';
import { useCollectedData } from '@/hooks/useCollectedData';
import type { StepProps } from '@/types/steps';
import styles from './TariffStep.module.css';

export const TariffStep = memo(function TariffStep({
  stepId,
  options,
  onSubmit,
  isSubmitting,
}: StepProps) {
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [query, setQuery] = useState('');

  const collectedData = useCollectedData();
  const previousTariffId = useMemo(() => {
    const group = stepId.startsWith('dso') ? 'dso' : 'retailer';
    const nested = collectedData[group];
    if (typeof nested === 'object' && nested !== null) {
      // New shape: { tariffs: [{ tariff_id, direction }] }
      const tariffs = (nested as Record<string, unknown>)['tariffs'];
      if (Array.isArray(tariffs) && tariffs.length > 0) {
        const first: unknown = tariffs[0];
        if (typeof first === 'object' && first !== null) {
          const id = (first as Record<string, unknown>)['tariff_id'];
          if (typeof id === 'string' || typeof id === 'number') return String(id);
        }
      }
    }
    return '';
  }, [collectedData, stepId]);

  const handleFilterChange = useCallback((key: string, valueId: string) => {
    setActiveFilters((prev) => {
      if (prev[key] === valueId) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: valueId };
    });
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      if (isSubmitting) return;
      onSubmit({ tariff_id: id });
    },
    [onSubmit, isSubmitting],
  );

  const { regularOptions, customOption } = useMemo(() => {
    const regular = options.options.filter((o) => o.metadata['is_custom'] !== true);
    const custom = options.options.find((o) => o.metadata['is_custom'] === true);
    return { regularOptions: regular, customOption: custom ?? null };
  }, [options.options]);

  const filterableKeys = useMemo(() => getFilterableKeys(regularOptions), [regularOptions]);

  const filteredRegularOptions = useMemo(() => {
    const byProperties = filterByProperties(regularOptions, activeFilters);
    if (!query) return byProperties;
    const lower = query.toLowerCase();
    return byProperties.filter((o) => o.name.toLowerCase().includes(lower));
  }, [regularOptions, activeFilters, query]);

  const hasListed = regularOptions.length > 0;
  const customLabel = hasListed
    ? t('steps.common.tariffNotListed')
    : t('steps.common.createTariff');

  const titleKey = `steps.${stepId}.title`;
  const subtitleKey = `steps.${stepId}.subtitle`;

  return (
    <>
      <StepHeading title={t(titleKey)} subtitle={t(subtitleKey)} icon={getStepIcon(stepId)} />
      {regularOptions.length > 6 && (
        <div className={styles['search']}>
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder={t('common.search')}
            autoFocus
          />
        </div>
      )}
      <PropertyFilters
        options={regularOptions}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
      />
      <div className={styles['list']} role="listbox" aria-label={t(titleKey)}>
        {filteredRegularOptions.map((o) => (
          <OptionCard
            key={o.id}
            id={o.id}
            name={o.name}
            badges={(
              o.metadata['properties'] as Array<{ key: string; value_name: string }> | undefined
            )
              ?.filter((p) => filterableKeys.has(p.key))
              .map((p) => p.value_name)}
            isSelected={o.id === previousTariffId}
            onClick={handleSelect}
          />
        ))}
      </div>
      {!hasListed && customOption && (
        <p className={styles['emptyState']}>{t('steps.common.noTariffsFound')}</p>
      )}
      {hasListed && filteredRegularOptions.length === 0 && (
        <p className={styles['emptyState']}>{t('steps.common.noTariffsMatch')}</p>
      )}
      {customOption && (
        <button
          type="button"
          className={styles['customCta']}
          onClick={() => handleSelect(customOption.id)}
          disabled={isSubmitting}
        >
          {customLabel} <span aria-hidden="true">→</span>
        </button>
      )}
    </>
  );
});
